/**
 * Тонкий typed WebSocket-wrapper для encounter-кімнати.
 * Auto-reconnect з backoff (1s → 2 → 4 → max 30s).
 */
import type { ClientMessage, ServerMessage } from '@sd/shared';

export type EncounterRole = 'gm' | 'player';

export interface ConnectOpts {
  encounterId: string;
  role: EncounterRole;
  /** GM-secret (role='gm') або session id (role='player') */
  credential: string;
  onMessage: (msg: ServerMessage) => void;
  onStatus?: (s: 'connecting' | 'open' | 'closed' | 'error') => void;
}

export class EncounterConnection {
  private socket: WebSocket | null = null;
  private opts: ConnectOpts;
  private closedByUser = false;
  private reconnectDelay = 1000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Час останнього успішного open — щоб не скидати backoff на flapping */
  private lastOpenAt = 0;
  /** Максимум послідовних спроб без stable-connection */
  private failedAttempts = 0;
  private readonly MAX_ATTEMPTS = 20;

  constructor(opts: ConnectOpts) {
    this.opts = opts;
    this.connect();
  }

  private connect(): void {
    if (this.closedByUser) return;
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const param = this.opts.role === 'gm' ? `gm=${encodeURIComponent(this.opts.credential)}` : `session=${encodeURIComponent(this.opts.credential)}`;
    const url = `${proto}//${window.location.host}/ws/encounter/${encodeURIComponent(this.opts.encounterId)}?${param}`;
    this.opts.onStatus?.('connecting');

    const ws = new WebSocket(url);
    this.socket = ws;

    ws.onopen = () => {
      this.lastOpenAt = Date.now();
      // Reset backoff ONLY якщо попередня сесія прожила хоча б 5 секунд —
      // інакше це flapping і ми не хочемо DoS-ити сервер 1-секундними спробами.
      // failedAttempts скидається тільки коли connection стане stable.
      this.opts.onStatus?.('open');
    };
    ws.onmessage = (e: MessageEvent<string>) => {
      try {
        const msg = JSON.parse(e.data) as ServerMessage;
        this.opts.onMessage(msg);
      } catch { /* noop */ }
    };
    ws.onerror = () => { this.opts.onStatus?.('error'); };
    ws.onclose = () => {
      this.opts.onStatus?.('closed');
      this.socket = null;
      if (this.closedByUser) return;

      // Якщо connection прожив хоча б 5 c — скидаємо лічильник.
      const wasStable = this.lastOpenAt > 0 && Date.now() - this.lastOpenAt > 5000;
      if (wasStable) {
        this.reconnectDelay = 1000;
        this.failedAttempts = 0;
      } else {
        this.failedAttempts++;
      }
      if (this.failedAttempts >= this.MAX_ATTEMPTS) {
        // Здаємось — UI може показати «Втрачено з'єднання, оновіть сторінку»
        this.opts.onStatus?.('error');
        return;
      }
      const delay = Math.min(this.reconnectDelay, 30000);
      // Зберігаємо handle, щоб close() міг його скасувати
      this.reconnectTimer = setTimeout(() => {
        this.reconnectTimer = null;
        this.connect();
      }, delay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
    };
  }

  send(msg: ClientMessage): boolean {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify(msg));
    return true;
  }

  close(): void {
    this.closedByUser = true;
    // КРИТИЧНО: скасувати запланований reconnect, інакше zombie-timer
    // викличе connect() після того, як React розмонтував компонент.
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }
}
